import React from "react";
import { SendOutlined } from "@ant-design/icons";
import { Alert, Button, DatePicker, Modal, Radio, Spin, message } from "antd";
import dayjs from "dayjs";

import { useState } from "react";
import { useDispatch } from "react-redux";
import { updateCampaign } from "../../redux/actions/campaignsAction";
import { privateGetApi } from "../../utils/apiHelper";

export default function SendEmail({ id, subject, fetchRecords }) {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendMode, setSendMode] = useState("now");
  const [scheduledSendAt, setScheduledSendAt] = useState(null);
  const [recipientPreview, setRecipientPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const showModal = () => {
    setSendMode("now");
    setScheduledSendAt(null);
    setIsModalOpen(true);
    setRecipientPreview(null);
    setPreviewLoading(true);
    privateGetApi(`/campaigns/${id}/recipients/preview`, {})
      .then((res) => setRecipientPreview(res?.data?.result || null))
      .catch(() => setRecipientPreview(null))
      .finally(() => setPreviewLoading(false));
  };

  const handleOk = () => {
    if (sendMode === "later" && !scheduledSendAt) {
      message.error("Please pick a date and time to schedule the send.");
      return;
    }

    setLoading(true);
    const payload =
      sendMode === "later"
        ? {
            status: { type: "warning", description: "Scheduled" },
            subject: subject,
            scheduledSendAt: scheduledSendAt.toISOString(),
          }
        : {
            status: { type: "processing", description: "Pending" },
            subject: subject,
          };

    dispatch(updateCampaign(payload, id))
      .then(() => {
        fetchRecords();
        setLoading(false);
        setIsModalOpen(false);
      })
      .catch((e) => {
        setLoading(false);
        console.log(e);
      });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Button
        type="primary"
        onClick={showModal}
        title="Send Email"
        loading={loading}
      >
        <SendOutlined />
      </Button>
      {isModalOpen && (
        <Modal
          title={"Send email"}
          open={isModalOpen}
          okText={
            sendMode === "later"
              ? "Schedule"
              : recipientPreview?.missingAttachment > 0
                ? "Send anyway"
                : "Yes, Send"
          }
          okType={sendMode === "later" ? "primary" : "danger"}
          cancelText="No"
          onOk={handleOk}
          onCancel={handleCancel}
        >
          <p>Choose when this campaign should go out.</p>
          {previewLoading && <Spin size="small" style={{ marginBottom: 16 }} />}
          {!previewLoading && recipientPreview && (
            <Alert
              type={recipientPreview.missingAttachment > 0 ? "warning" : "info"}
              showIcon
              style={{ marginBottom: 16 }}
              message={`${recipientPreview.totalContacts} recipient(s) match this campaign's tags.`}
              description={
                <>
                  {recipientPreview.missingAttachment > 0 && (
                    <p style={{ margin: 0 }}>
                      {recipientPreview.missingAttachment} of them have no
                      per-contact attachment and will only receive this
                      campaign's shared attachment(s), if any.
                    </p>
                  )}
                  {(recipientPreview.suppressedExcluded > 0 ||
                    recipientPreview.inactiveExcluded > 0) && (
                    <p style={{ margin: 0 }}>
                      {recipientPreview.suppressedExcluded > 0 &&
                        `${recipientPreview.suppressedExcluded} suppressed (bounced/complained/unsubscribed)`}
                      {recipientPreview.suppressedExcluded > 0 &&
                        recipientPreview.inactiveExcluded > 0 &&
                        " / "}
                      {recipientPreview.inactiveExcluded > 0 &&
                        `${recipientPreview.inactiveExcluded} inactive`}
                      {" excluded automatically."}
                    </p>
                  )}
                </>
              }
            />
          )}
          <Radio.Group
            value={sendMode}
            onChange={(e) => setSendMode(e.target.value)}
            style={{ marginBottom: 16 }}
          >
            <Radio value="now">Send now</Radio>
            <Radio value="later">Schedule for later</Radio>
          </Radio.Group>
          {sendMode === "later" && (
            <DatePicker
              showTime
              style={{ width: "100%" }}
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
              value={scheduledSendAt}
              onChange={setScheduledSendAt}
              placeholder="Select send date & time"
            />
          )}
        </Modal>
      )}
    </>
  );
}
